// ============================================================================
// Gallery photo ordering
//
// Photos only carry a coarse category in `title` ("Community" / "Apartment"),
// so the subject (pool, kitchen, bedroom, …) is inferred from keywords in the
// photo's alt text + description. Each photo lands in one bucket; buckets are
// shown in display order, and within a bucket staged / stronger shots lead
// while weak ones (toilet-focused, unstaged, empty rooms) trail. Photos whose
// caption flags them as unusable (blurry, dirty, empty closet, …) are dropped.
// ============================================================================

export type GalleryScope = 'Community' | 'Apartment';

interface Bucket {
  id: string;
  /** Subject words — the earliest one mentioned in the caption wins. */
  strong?: string;
  /** Generic words — only consulted when no strong word matches. */
  weak?: string;
  /** Buckets this one beats whenever both match ("kitchen island" → island). */
  beats?: string[];
}

// Display order (also the tie-break when two words start at the same index).
const COMMUNITY_BUCKETS: Bucket[] = [
  // Property exterior
  { id: 'exterior', strong: 'exteriors?|fa[cç]ades?|aerial|curb appeal|street view|property view', weak: 'buildings?|architecture|townhomes?|community view' },
  { id: 'signage', strong: 'signs?|signage|monument|marquee' },
  { id: 'entrance', strong: 'entrances?|entry|entryway|gated?|porte[- ]coch[eè]re|front doors?|driveway|motor court|arrival' },
  { id: 'architectural', strong: 'architectural|stonework|archways?|portico', weak: 'details?|columns?' },
  // Outdoor community
  { id: 'pool', strong: 'pools?(?! tables?)|poolside|swimming|sundeck|sun deck|hot tub|jacuzzi|whirlpool|splash pad|cabanas?', weak: 'lounge chairs?' },
  { id: 'grill', strong: 'grills?|grilling|bbq|barbecue|fire ?pits?|outdoor kitchen|picnic|outdoor dining|outdoor lounge|pergola|gazebo|pavilion', weak: 'entertain(?:ing|ment)' },
  { id: 'playground', strong: 'playgrounds?|play area|tot lot|play structure' },
  { id: 'recreation', strong: 'recreation(?:al)?|game lawn|lawn games|bocce|putting green|cornhole|horseshoes?|disc golf' },
  { id: 'courts', strong: 'tennis|basketball|pickleball|volleyball|racquetball|sport courts?', weak: 'courts?' },
  { id: 'dogpark', strong: 'dog ?parks?|bark park|pet park|dog run|off[- ]leash|pet play(?:ground)?' },
  { id: 'outdoorOther', weak: 'courtyard|gardens?|landscap(?:e|ed|ing)|trails?|walking path|lake|pond|fountain|green ?space|lawn|outdoor|rooftop|roof deck|hammocks?|parking|garage|carports?|car wash|ev charging' },
  // Indoor community
  { id: 'clubhouse', strong: 'clubhouse|club ?room|resident lounge|lounge|social room|community room|gathering room|resident kitchen|demonstration kitchen' },
  { id: 'lobby', strong: 'lobby|leasing (?:office|center)|reception|concierge|front desk|mail ?room', weak: 'hallways?|corridors?' },
  { id: 'business', strong: 'business cent(?:er|re)|co-?working|work ?spaces?|work from home|conference|meeting room|study|private offices?', weak: 'offices?' },
  { id: 'theater', strong: 'theat(?:er|re)|media room|cinema|screening room|movie' },
  { id: 'fitness', strong: 'fitness|gym|workout|exercise|yoga|weights?|cardio|treadmills?|peloton|spin room' },
  { id: 'spa', strong: 'spa|sauna|steam room|massage|tanning' },
  { id: 'package', strong: 'packages?|parcels?|luxer|amazon hub', weak: 'lockers?' },
  { id: 'petwash', strong: 'pet wash(?:ing)?|dog wash|pet spa|dog spa|grooming|pet station', beats: ['spa'] },
  { id: 'indoorOther', strong: 'game room|billiards?|pool tables?|library|arcade|bike room|wine room|kids room|playroom', weak: 'indoor|elevators?|storage' },
];

const APARTMENT_BUCKETS: Bucket[] = [
  { id: 'kitchen', strong: 'kitchens?|pantry', weak: 'appliances?|cabinets?|cabinetry|counter ?tops?|counters?|backsplash|stainless|range|oven|refrigerator|fridge|dishwasher|microwave|quartz|granite' },
  { id: 'island', strong: 'islands?|peninsulas?|breakfast bar|bar seating', beats: ['kitchen'] },
  { id: 'living', strong: 'living|family room|great room|den', weak: 'lounge|fireplace|open (?:floor ?plan|concept)|entertainment' },
  { id: 'dining', strong: 'dining|dinette|breakfast nook' },
  { id: 'bedroom', strong: 'bedrooms?|beds?|primary suite|master suite|guest room|nursery' },
  { id: 'bathroom', strong: 'bath|baths|bathrooms?|shower|vanity|vanities|bathtubs?|tub|soaking|toilets?|powder room|en-?suite' },
  { id: 'closet', strong: 'closets?|walk-in|wardrobe' },
  { id: 'laundry', strong: 'washers?|dryers?|laundry|w/d', beats: ['closet'] },
  { id: 'patio', strong: 'patios?|balcon(?:y|ies)|terraces?|decks?|porch|sunroom|lanai', weak: 'yard' },
  { id: 'apartmentOther' },
];

const wordRe = (src: string) => new RegExp(`\\b(?:${src})\\b`, 'i');

interface CompiledBucket extends Bucket {
  rank: number;
  strongRe?: RegExp;
  weakRe?: RegExp;
}

const compile = (buckets: Bucket[]): CompiledBucket[] =>
  buckets.map((b, rank) => ({
    ...b,
    rank,
    strongRe: b.strong ? wordRe(b.strong) : undefined,
    weakRe: b.weak ? wordRe(b.weak) : undefined,
  }));

const COMPILED: Record<GalleryScope, CompiledBucket[]> = {
  Community: compile(COMMUNITY_BUCKETS),
  Apartment: compile(APARTMENT_BUCKETS),
};

// Captions that mark a photo as not worth showing at all.
const REJECT = wordRe('blurry|blurred|out of focus|pixelated|low[- ]res(?:olution)?|poorly lit|under construction|renovation in progress|placeholder|coming soon|dirty|messy|cluttered|debris|trash|dumpsters?');
const EMPTY = wordRe('empty|bare|vacant|unfurnished|unstaged');
const STAGED = wordRe('staged|furnished|model|decorated|styled');
const STRONG = wordRe('spacious|bright|natural light|sunlit|stunning|modern|updated|renovated|upgraded|luxury|resort[- ]style|panoramic|views?|twilight|dusk|sparkling');
const TOILET = wordRe('toilets?|commode');
const BATH_FEATURE = wordRe('vanity|vanities|shower|tub|bathtub|soaking|tiled?|mirrors?|double sinks?|dual sinks?');

interface OrderablePhoto {
  image: string;
  alt_text?: string | null;
  description?: string | null;
}

function captionOf(p: OrderablePhoto): string {
  return `${p.alt_text || ''} ${p.description || ''}`.replace(/\s+/g, ' ').trim();
}

/** Bucket id for a caption, or null when nothing matches. */
function classify(caption: string, buckets: CompiledBucket[]): string | null {
  for (const tier of ['strongRe', 'weakRe'] as const) {
    const hits = buckets
      .map((b) => ({ b, pos: b[tier] ? caption.search(b[tier]!) : -1 }))
      .filter((h) => h.pos >= 0)
      .sort((x, y) => x.pos - y.pos || x.b.rank - y.b.rank);
    if (hits.length === 0) continue;
    const winner = hits[0].b;
    const overrider = hits.find((h) => h.b.beats?.includes(winner.id));
    return (overrider?.b ?? winner).id;
  }
  return null;
}

/** Higher = shown earlier within its bucket. */
function strength(caption: string, bucket: string | null): number {
  let score = 0;
  if (STAGED.test(caption) && !/\bunstaged\b/i.test(caption)) score += 3;
  if (EMPTY.test(caption)) score -= 3;
  if (STRONG.test(caption)) score += 1;
  if (bucket === 'bathroom' && TOILET.test(caption)) {
    score -= BATH_FEATURE.test(caption) ? 2 : 6;
  }
  return score;
}

function isWeak(caption: string, bucket: string | null): boolean {
  if (REJECT.test(caption)) return true;
  // An empty closet / patio shows nothing worth looking at.
  return (bucket === 'closet' || bucket === 'patio') && EMPTY.test(caption);
}

/**
 * Order (and prune) one scope's photos per the gallery's category sequence.
 * Duplicate URLs are collapsed. If every photo would be pruned the unpruned
 * set is kept so the tab never goes blank because of over-eager captions.
 */
export function orderGalleryPhotos<T extends OrderablePhoto>(photos: T[], scope: GalleryScope): T[] {
  const buckets = COMPILED[scope];
  const rankOf = new Map(buckets.map((b) => [b.id, b.rank]));

  const seen = new Set<string>();
  const entries = photos
    .filter((p) => {
      if (seen.has(p.image)) return false;
      seen.add(p.image);
      return true;
    })
    .map((photo, index) => {
      const caption = captionOf(photo);
      const bucket = classify(caption, buckets);
      return {
        photo,
        index,
        // Unrecognized photos trail the scope: after indoor amenities for
        // Community, alongside "other interior features" for Apartment.
        rank: bucket ? rankOf.get(bucket)! : buckets.length,
        score: strength(caption, bucket),
        weak: isWeak(caption, bucket),
      };
    });

  const kept = entries.filter((e) => !e.weak);
  return (kept.length > 0 ? kept : entries)
    .sort((a, b) => a.rank - b.rank || b.score - a.score || a.index - b.index)
    .map((e) => e.photo);
}
